import { NavLink, Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LogOut, LayoutDashboard, Sparkles, MessageSquare, BarChart3, Users,
  Building2, Bell, BookOpenCheck, History, UserCircle,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Wordmark } from './Wordmark';
import { cn } from '@/lib/cn';

const NAV_BY_ROLE = {
  super_admin: [
    { to: '/dashboard', label: 'Overview', icon: LayoutDashboard },
    { to: '/admin/isds', label: 'School Districts', icon: Building2 },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/teacher-suite', label: 'AI Tools', icon: Sparkles },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  ],
  isd_admin: [
    { to: '/dashboard', label: 'District', icon: LayoutDashboard },
    { to: '/admin/users', label: 'Staff', icon: Users },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  ],
  school_admin: [
    { to: '/dashboard', label: 'School', icon: LayoutDashboard },
    { to: '/teacher-suite', label: 'AI Tools', icon: Sparkles },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  ],
  teacher: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/teacher-suite', label: 'AI Tools', icon: Sparkles },
    { to: '/teacher-suite/history', label: 'History', icon: History },
  ],
  student: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/sparkbot', label: 'SparkBot', icon: MessageSquare },
    { to: '/learn',    label: 'Academy',   icon: BookOpenCheck },
  ],
  parent: [{ to: '/dashboard', label: 'My Child', icon: LayoutDashboard }],
};

function SidebarLink({ to, label, Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end ?? to === '/dashboard'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
          isActive
            // Tri-color active state mirrors the Sparknation logo atom — no pure-purple button
            ? 'bg-gradient-to-r from-purple-600 via-orange-500 to-green-500 text-white shadow-md'
            : 'text-gray-700 hover:bg-orange-50 hover:text-gray-900',
        )
      }
    >
      <Icon size={16} />
      {label}
    </NavLink>
  );
}

function NotifBell() {
  const { data: items = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/api/v1/notifications/mine/')).data,
    refetchInterval: 60_000,
  });
  const unread = items.filter((n) => !n.is_read).length;
  return (
    <Link to="/notifications" className="relative inline-flex items-center text-slate-600 hover:text-brand-ink">
      <Bell size={18} />
      {unread > 0 && (
        <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-accent px-1 text-[10px] font-bold text-white">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Link>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = NAV_BY_ROLE[user?.role] ?? [];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-purple-50/30 via-white to-orange-50/30">
      <aside className="w-64 shrink-0 border-r border-purple-100 bg-white p-5">
        <Link to="/dashboard"><Wordmark size="md" /></Link>
        <nav className="mt-8 space-y-1.5">
          {links.map((l) => (
            <SidebarLink key={l.to} to={l.to} label={l.label} Icon={l.icon} />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-purple-100 bg-white/80 backdrop-blur-sm px-6">
          <span className="text-sm text-gray-600">
            <strong className="text-gray-900">{user?.email}</strong>
            <span className="ml-2 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
              {user?.role}
            </span>
          </span>
          <div className="flex items-center gap-5">
            <NotifBell />
            <Link to="/profile" className="text-gray-600 hover:text-purple-600 transition-colors" aria-label="Profile">
              <UserCircle size={20} />
            </Link>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-purple-600 transition-colors font-medium"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
